import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Icon,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { BsLightningCharge } from "react-icons/bs";
import { TbCopy, TbMessageChatbot, TbStack2 } from "react-icons/tb";
import { FaMicrophone } from "react-icons/fa";
import { MdDelete, MdEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchController } from "../../utils/FetchController/fetchController";
import Loader from "../../utils/Loader/Loader";
import Paginator from "../../utils/Loader/Paginator";
import PaginationSizeSelect from "../../utils/Loader/PaginationSizeSelect";
import { getHostDomain } from "../../utils/utils";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import CreateAssistantModal from "./CreateAssistantModal";

const isVoice = (type) => type === "phone" || type === "realtime_openai";

const AssistantCard = ({ item, onEdit, onDelete, onDuplicate, buttonColorScheme }) => {
  const voice = isVoice(item.assistant_type);

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      p={5}
      bg="white"
      boxShadow="sm"
      _hover={{ boxShadow: "md" }}
      display="flex"
      flexDirection="column"
      gap={3}
    >
      {/* Header */}
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Text fontWeight="bold" fontSize="md" noOfLines={1}>
            {item.name}
          </Text>
          <Badge
            mt={1}
            colorScheme={voice ? "purple" : "blue"}
            variant="subtle"
            fontSize="10px"
            display="flex"
            alignItems="center"
            gap={1}
            w="fit-content"
          >
            <Icon as={voice ? FaMicrophone : TbMessageChatbot} />
            {voice ? "VOICE ASSISTANT" : "CHAT ASSISTANT"}
          </Badge>
        </Box>
      </Flex>

      {/* Stats */}
      <Grid templateColumns="repeat(3, 1fr)" gap={2} py={2} borderTop="1px solid" borderColor="gray.100">
        <Box textAlign="center">
          <Flex direction="column" align="center">
            <Icon as={TbStack2} color="orange.400" boxSize={5} />
            <Text fontWeight="bold" fontSize="lg" lineHeight={1.2}>
              {item.data_sources_count ?? 0}
            </Text>
            <Text fontSize="10px" color="gray.500" textTransform="uppercase">
              Sources
            </Text>
          </Flex>
        </Box>
        <Box textAlign="center">
          <Flex direction="column" align="center">
            <Icon as={BsLightningCharge} color="orange.400" boxSize={5} />
            <Text fontWeight="bold" fontSize="lg" lineHeight={1.2}>
              {item.tools_count ?? 0}
            </Text>
            <Text fontSize="10px" color="gray.500" textTransform="uppercase">
              Tools
            </Text>
          </Flex>
        </Box>
        <Box textAlign="center">
          <Flex direction="column" align="center">
            <Icon as={TbMessageChatbot} color="green.400" boxSize={5} />
            <Text fontWeight="bold" fontSize="lg" lineHeight={1.2}>
              {item.forms_count ?? 0}
            </Text>
            <Text fontSize="10px" color="gray.500" textTransform="uppercase">
              Forms
            </Text>
          </Flex>
        </Box>
      </Grid>

      {/* Footer actions */}
      <Flex justifyContent="space-between" alignItems="center" pt={1} borderTop="1px solid" borderColor="gray.100">
        <Button
          variant="link"
          colorScheme={buttonColorScheme}
          size="sm"
          leftIcon={<MdEdit />}
          onClick={() => onEdit(item.id)}
        >
          Edit Assistant
        </Button>
        <Flex gap={1}>
          <Tooltip label="Duplicate">
            <IconButton
              icon={<TbCopy />}
              size="xs"
              variant="ghost"
              aria-label="Duplicate"
              onClick={() => onDuplicate(item)}
            />
          </Tooltip>
          <Tooltip label="Delete">
            <IconButton
              icon={<MdDelete />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              aria-label="Delete"
              onClick={() => onDelete(item.id)}
            />
          </Tooltip>
        </Flex>
      </Flex>
    </Box>
  );
};

const AssistantsList2 = () => {
  const baseUrl = getHostDomain();
  const navigate = useNavigate();
  const agency = useContext(AgencyContext);
  const { textColor, buttonColorScheme } = agency;

  const [status, setStatus] = useState("idle");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [paginationSize, setPaginationSize] = useState(12);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const hitFetch = async () => {
    try {
      setStatus("loading");
      const res = await fetchController(
        baseUrl + `/api/v1/assistant/get_my_details?size=${paginationSize}&page=${page}`,
        "GET"
      );
      setPages(res?.data?.pages ?? 0);
      setItems(res?.data?.items ?? []);
      setStatus("success");
    } catch {
      setStatus("failure");
    }
  };

  useEffect(() => {
    hitFetch();
  }, [page, paginationSize]);

  const handleEdit = (id) => navigate(`/ai-agents/assistants2/${id}`);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this assistant?")) return;
    try {
      await fetchController(baseUrl + `/api/v1/assistant/${id}`, "DELETE");
      toast.success("Assistant deleted");
      hitFetch();
    } catch {
      toast.error("Failed to delete assistant");
    }
  };

  const handleDuplicate = async (item) => {
    try {
      const res = await fetchController(
        baseUrl + `/api/v1/assistant/${item.id}/clone`,
        "POST"
      );
      if (res?.data?.id) {
        toast.success("Assistant duplicated");
        hitFetch();
      }
    } catch {
      toast.error("Failed to duplicate assistant");
    }
  };

  const handleCreated = (id) => {
    onClose();
    navigate(`/ai-agents/assistants2/${id}`);
  };

  return (
    <Box h="100%" w="100%" overflowY="auto" p={4}>
      {/* Header */}
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Box>
          <Heading color={textColor} fontSize="2xl">
            My Assistants
          </Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Manage and build your conversational and voice AI agents.
          </Text>
        </Box>
        <Button colorScheme={buttonColorScheme} onClick={onOpen} leftIcon={<span>+</span>}>
          Create Assistant
        </Button>
      </Flex>

      {/* Grid */}
      {status === "loading" ? (
        <Flex justify="center" align="center" h="60%">
          <Spinner size="xl" />
        </Flex>
      ) : items.length === 0 ? (
        <Flex justify="center" align="center" h="60%" direction="column" gap={3}>
          <Text color="gray.400" fontSize="lg">No assistants yet.</Text>
          <Button colorScheme={buttonColorScheme} onClick={onOpen}>
            Create your first assistant
          </Button>
        </Flex>
      ) : (
        <Grid templateColumns="repeat(auto-fill, minmax(280px, 1fr))" gap={4}>
          {items.map((item) => (
            <AssistantCard
              key={item.id}
              item={item}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              buttonColorScheme={buttonColorScheme}
            />
          ))}
        </Grid>
      )}

      {/* Pagination */}
      <Flex justifyContent="flex-end" alignItems="center" mt={4} gap={2}>
        <PaginationSizeSelect
          paginationSize={paginationSize}
          setPaginationSize={setPaginationSize}
          tableLength={items.length}
        />
        <Paginator
          colorScheme={buttonColorScheme}
          onChangePage={setPage}
          page={page}
          pages={pages}
        />
      </Flex>

      {/* Create modal */}
      {isOpen && (
        <CreateAssistantModal
          isOpen={isOpen}
          onClose={onClose}
          onCreated={handleCreated}
        />
      )}
    </Box>
  );
};

export default AssistantsList2;
